import type { AxiosError } from 'axios'
import type { Path, UseFormSetError } from 'react-hook-form'
import { toast } from '@/components/ui/Toast'

/**
 * Formato típico de erro do Laravel:
 *   422 → { message: "validation.integer", errors: { campo_x: ["msg1", "msg2"], ... } }
 *   401 → { message: "Unauthenticated." }
 *   500 → { message: "Server error" }
 */
interface LaravelErrorBody {
  message?: string
  errors?: Record<string, string[]>
}

export interface ExtractedApiError {
  message: string
  fields: Record<string, string>
  /** Lista [campo, mensagem] na ordem original do backend. */
  fieldsList: Array<{ field: string; message: string }>
  firstFieldMessage: string | null
}

const TRANSLATIONS: Record<string, string> = {
  'validation.required':  'Campo obrigatório.',
  'validation.integer':   'Informe um número inteiro válido.',
  'validation.numeric':   'Informe um número válido.',
  'validation.string':    'Informe um texto válido.',
  'validation.email':     'E-mail inválido.',
  'validation.unique':    'Já existe um registro com este valor.',
  'validation.date':      'Data inválida.',
  'validation.in':        'Valor não permitido.',
  'validation.between':   'Valor fora do intervalo permitido.',
  'validation.max':       'Valor muito grande.',
  'validation.min':       'Valor muito pequeno.',
  'validation.gte':       'Valor menor que o permitido.',
  'validation.lte':       'Valor maior que o permitido.',
  'validation.size':      'Tamanho incorreto.',
  'validation.exists':    'Registro relacionado não encontrado.',
  'validation.uuid':      'Identificador inválido.',
  'validation.boolean':   'Valor deve ser verdadeiro ou falso.',
  'validation.accepted':  'É preciso aceitar este item.',
  'Unauthenticated.':     'Sessão expirada — faça login novamente.',
}

/** Labels amigáveis para nomes de campo do backend. Expansível conforme uso. */
const FIELD_LABELS: Record<string, string> = {
  'name':                        'Nome',
  'monthly_installments':        'Por quantas parcelas?',
  'monthly_plans_need_sold':     'Meta de planos vendidos mensalmente',
  'monthly_contract_sales_goal': 'Meta de contratos por mês',
  'status':                      'Status',
  'installment_from':            'Parcela de',
  'installment_to':              'Parcela até',
  'installment_action':          'Tipo',
  'commission_type':             'Tipo de comissão',
  'value':                       'Valor',
  'user_type':                   'Cargo',
  'contract_from':               'Contrato de',
  'contract_to':                 'Contrato até',
  'contract_action':             'Tipo',
}

function humanize(raw: string): string {
  return TRANSLATIONS[raw] ?? raw
}

function labelFor(field: string): string {
  // Backend pode responder em snake_case OU camelCase (depende do middleware de
  // conversão). Tentamos as duas formas — e como fallback caímos no próprio
  // campo "humanizado" (snake → espaços).
  const snake = camelToSnake(field)
  return FIELD_LABELS[field] ?? FIELD_LABELS[snake] ?? snake.replace(/_/g, ' ')
}

function camelToSnake(s: string): string {
  return s.replace(/([A-Z])/g, '_$1').toLowerCase()
}

export function extractApiError(e: unknown): ExtractedApiError {
  const ax = e as AxiosError<LaravelErrorBody>
  const data = ax?.response?.data
  const rawMessage = data?.message ?? (e instanceof Error ? e.message : 'Erro inesperado.')
  const fields: Record<string, string> = {}
  const fieldsList: ExtractedApiError['fieldsList'] = []
  let firstFieldMessage: string | null = null

  if (data?.errors && typeof data.errors === 'object') {
    for (const [key, msgs] of Object.entries(data.errors)) {
      if (Array.isArray(msgs) && msgs.length > 0) {
        const friendly = humanize(msgs[0])
        fields[key] = friendly
        fieldsList.push({ field: key, message: friendly })
        if (firstFieldMessage === null) firstFieldMessage = friendly
      }
    }
  }

  // Log para debug em dev — facilita encontrar campos novos a mapear.
  if (import.meta.env.DEV && (data || ax?.response)) {
    // eslint-disable-next-line no-console
    console.warn('[api-error]', {
      status: ax?.response?.status,
      url: ax?.config?.url,
      method: ax?.config?.method,
      message: data?.message,
      errors: data?.errors,
    })
  }

  return {
    message: firstFieldMessage ?? humanize(rawMessage),
    fields,
    fieldsList,
    firstFieldMessage,
  }
}

/**
 * Trata erro de submissão:
 *  - Aplica erros em `setError` quando o campo é conhecido no form
 *  - Toast com TODAS as mensagens (uma por linha), incluindo o nome amigável
 *    do campo — assim o usuário sempre sabe o que faltou
 *  - Em dev, loga o payload de erro original no console
 */
export function applyApiErrorsToForm<T extends Record<string, unknown>>(
  e: unknown,
  setError: UseFormSetError<T>,
  /** Mapa opcional: { 'monthly_installments': 'installments' } */
  fieldMap?: Record<string, string>,
): ExtractedApiError {
  const ext = extractApiError(e)

  // Aplica setError campo a campo (mesmo que o campo não esteja visível — RHF
  // ignora silenciosamente quando o name não é registrado).
  for (const [snakeKey, msg] of Object.entries(ext.fields)) {
    const camelKey = fieldMap?.[snakeKey] ?? snakeToCamel(snakeKey)
    setError(camelKey as Path<T>, { type: 'server', message: msg })
  }

  // Monta mensagem do toast: lista cada campo + mensagem amigável.
  let description: string
  if (ext.fieldsList.length === 0) {
    description = ext.message
  } else if (ext.fieldsList.length === 1) {
    const f = ext.fieldsList[0]
    description = `${labelFor(f.field)}: ${f.message}`
  } else {
    description = ext.fieldsList
      .map((f) => `• ${labelFor(f.field)}: ${f.message}`)
      .join('\n')
  }

  toast.error({
    title: 'Não foi possível salvar',
    description,
  })
  return ext
}

function snakeToCamel(key: string): string {
  return key.replace(/_([a-z])/g, (_m, ch) => ch.toUpperCase())
}
